<script setup lang="ts">
/**
 * What this assistant reads — as things with names.
 *
 * An assistant's reach is set by content-scope tags, and tags are the hardest
 * idea in the product: `offers` means nothing until you have read the
 * registry. But every tag is fed by something a person can name — the Bid
 * Office library, the contract register — and that is how anyone actually
 * thinks about what an assistant knows. So the editor shows that, and the
 * tags move to Advanced, where the few who configure them will find them.
 *
 * Read-only on purpose. What an assistant reads is changed in two places
 * that already exist: Data sources (add or remove a source) and the content
 * scope under Advanced. A third way to change it here would be one more
 * thing to understand, which is the opposite of the point.
 */
import { onMounted, ref, watch } from "vue";
import { type AssistantRead, getAssistantReads } from "../api/admin";

const props = defineProps<{ slug: string; devUser: string }>();

const reads = ref<AssistantRead[] | null>(null);
const failed = ref(false);

const GATE: Record<AssistantRead["enforcement"], string> = {
  mirrored: "each person sees what they can see in the source",
  assigned: "each person sees what they have been given here",
  own: "each person sees only their own",
  group: "everyone who can use the assistant sees all of it",
  open: "public",
  undeclared: "nobody has recorded how it is gated",
};

async function load(): Promise<void> {
  failed.value = false;
  try {
    reads.value = (await getAssistantReads(props.slug, props.devUser)).reads;
  } catch {
    // Explanation, not function: failing to load it must not get in the way
    // of editing the assistant.
    failed.value = true;
  }
}

onMounted(load);
watch(() => props.slug, load);

function goToSources(): void {
  window.location.hash = "#/admin?tab=data-sources";
}
</script>

<template>
  <div class="reads">
    <p v-if="failed" class="muted">Could not work out what it reads.</p>
    <p v-else-if="reads === null" class="muted">Loading…</p>
    <template v-else>
      <ul v-if="reads.length" class="reads__list">
        <li v-for="r in reads" :key="`${r.title}:${r.via}`" class="read">
          <span class="read__title">{{ r.title }}</span>
          <span class="read__gate" :class="`read__gate--${r.enforcement}`">{{
            GATE[r.enforcement]
          }}</span>
          <span v-if="r.detail || r.via" class="read__detail">
            {{ [r.detail, r.via].filter(Boolean).join(" · ") }}
          </span>
        </li>
      </ul>
      <p v-else class="muted">
        Nothing yet — it answers from the model alone until a source is added.
      </p>
      <p class="reads__foot">
        <button type="button" class="link" @click="goToSources">
          Add or change sources
        </button>
        in Data sources.
      </p>
    </template>
  </div>
</template>

<style scoped>
.reads {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}
.reads__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.read {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.1rem 0.75rem;
  align-items: baseline;
}
.read__title {
  font-weight: 600;
}
.read__gate {
  font-size: 0.8rem;
  color: var(--color-muted);
  text-align: right;
}
/* The two states worth a second look: one group sees everything, or nobody
   has said. Colour reinforces the words; it never replaces them. */
.read__gate--group,
.read__gate--undeclared {
  color: var(--color-warn-ink);
}
.read__detail {
  grid-column: 1 / -1;
  font-size: 0.8rem;
  color: var(--color-muted);
}
.reads__foot,
.muted {
  margin: 0;
  font-size: 0.85rem;
  color: var(--color-muted);
}
button.link {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: var(--color-accent);
  cursor: pointer;
}
</style>
